import React, { useRef } from 'react';
import clsx from 'clsx';
import { useSanitize } from '../../hooks/useSanitize';
import * as Styled from './ABody.styles';
import { ABodyProps, ABodySize, ABodyColor } from './ABody.types';

const COMPONENT_NAME = 'a-web-ui-kit-ABody';
const TEST_ID = 'web-ui-kit-ABody';

// This is an intentionally permissive list of "allowed tags" for Rich Text
// content, given that ABody is a common atom. The list may be pared down based
// on security reviews and use cases.
const ABodyAllowedTags = [
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'del',
  'sub',
  'sup',
  'mark',
  'small',
  'big',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'table',
  'tr',
  'td',
  'th',
  'thead',
  'tbody',
  'tfoot',
  'colgroup',
  'col',
  'caption',
  'p',
  'br',
  'hr',
  'blockquote',
  'pre',
  'a',
  'code',
  'span',
  'div',
  'img',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
];
const sanitizeOpts = { ALLOWED_TAGS: ABodyAllowedTags };

function ABody({ size = ABodySize.S, color = ABodyColor.Default, bodyText, className, ...rest }: ABodyProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mergedClassName = clsx(
    className,
    color === ABodyColor.Default && {
      't-font-s': size === ABodySize.S,
      't-font-xs': size === ABodySize.XS,
      't-font-m': size === ABodySize.M,
    },
    color === ABodyColor.Alternate && {
      't-font-alt-s': size === ABodySize.S,
      't-font-alt-xs': size === ABodySize.XS,
      't-font-alt-m': size === ABodySize.M,
    }
  );

  const { isDangerouslySet, result: cleanBodyText } = useSanitize(bodyText, sanitizeOpts);

  return isDangerouslySet ? (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      ref={containerRef}
      className={mergedClassName}
      {...rest}
      $rte={isDangerouslySet}
      dangerouslySetInnerHTML={{ __html: cleanBodyText }}
    />
  ) : (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      ref={containerRef}
      as="p"
      className={mergedClassName}
      {...rest}
    >
      {cleanBodyText}
    </Styled.Wrapper>
  );
}

export { ABody, ABodySize, ABodyColor, TEST_ID as A_BODY_TEST_ID };
